import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Key from '@mui/icons-material/Key';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../api/axios';
import LoadingIndicator from '../components/LoadingIndicator';
import PageHeader from '../components/PageHeader';
import { documentStatusChipSx } from '../components/documentStatusChip';

const ROLE_OPTIONS = [
  ['ADMINISTRATOR', 'Administrator'],
  ['VERWALTER', 'Verwalter'],
  ['MITGLIED', 'Mitglied'],
];
const roleLabel = code => ROLE_OPTIONS.find(([key]) => key === code)?.[1] || code;
const failure = error => error.response?.data?.error || 'Benutzerverwaltung konnte nicht verarbeitet werden.';

function PasswordField({ label, value, onChange, autoComplete = 'new-password' }) {
  const [visible, setVisible] = useState(false);
  return <TextField required label={label} type={visible ? 'text' : 'password'} value={value}
    onChange={event => onChange(event.target.value)} autoComplete={autoComplete} inputProps={{ maxLength: 1024 }}
    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton edge="end"
      aria-label={visible ? `${label} ausblenden` : `${label} anzeigen`}
      onClick={() => setVisible(current => !current)} onMouseDown={event => event.preventDefault()}>
      {visible ? <VisibilityOff /> : <Visibility />}
    </IconButton></InputAdornment> }} />;
}

function RoleSelect({ value, onChange, label = 'Rollen' }) {
  const id = useId();
  return <FormControl size="small" sx={{ minWidth: 210 }}><InputLabel id={`${id}-label`}>{label}</InputLabel><Select multiple
    id={id} labelId={`${id}-label`} label={label} value={value} onChange={event => onChange(event.target.value)}
    renderValue={selected => selected.map(roleLabel).join(', ')}>
    {ROLE_OPTIONS.map(([code, name]) => <MenuItem key={code} value={code}><Checkbox checked={value.includes(code)} />
      <ListItemText primary={name} /></MenuItem>)}
  </Select></FormControl>;
}

function PasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.put(`/sysadmin/benutzer/${user.Id}/passwort`, { Passwort: password, PasswortWiederholung: confirmation });
      onClose(true);
    } catch (reason) { setError(failure(reason)); } finally { setBusy(false); }
  };
  return <Dialog open onClose={busy ? undefined : () => onClose(false)} fullWidth maxWidth="xs">
    <DialogTitle>Passwort für {user.Benutzername} setzen</DialogTitle><DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>{error && <Alert severity="error">{error}</Alert>}
        <PasswordField label="Neues Passwort" value={password} onChange={setPassword} />
        <PasswordField label="Passwort wiederholen" value={confirmation} onChange={setConfirmation} />
        <Typography variant="caption" color="text.secondary">Mindestens 8 Zeichen, eine Ziffer und ein Sonderzeichen.</Typography>
      </Stack>
    </DialogContent><DialogActions><Button disabled={busy} onClick={() => onClose(false)}>Abbrechen</Button>
      <Button variant="contained" disabled={busy || !password || !confirmation} onClick={save}>Passwort setzen</Button>
    </DialogActions>
  </Dialog>;
}

function UserDialog({ user, tenants, onClose, onChanged }) {
  const creating = !user;
  const [form, setForm] = useState(user ? { ...user } : {
    Benutzername: '', Anzeigename: '', Email: '', Status: 'Aktiv', Passwort: '', PasswortWiederholung: '', Mandanten: [],
  });
  const [newTenant, setNewTenant] = useState('');
  const [newRoles, setNewRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const availableTenants = tenants.filter(tenant => !form.Mandanten.some(item => item.Mandant_Id === tenant.Id));

  const saveUser = async () => {
    setBusy(true); setError('');
    try {
      if (creating) await api.post('/sysadmin/benutzer', { ...form, Mandanten: form.Mandanten.map(assignment => ({
        Mandant_Id: assignment.Mandant_Id, Rollen: assignment.Rollen.map(role => role.Kuerzel),
      })) });
      else await api.put(`/sysadmin/benutzer/${form.Id}`, {
        Benutzername: form.Benutzername, Anzeigename: form.Anzeigename || null, Email: form.Email, Status: form.Status,
      });
      onChanged();
    } catch (reason) { setError(failure(reason)); } finally { setBusy(false); }
  };

  const addAssignment = async () => {
    if (!newTenant || !newRoles.length) return;
    const tenant = tenants.find(item => item.Id === Number(newTenant));
    if (creating) {
      setForm(current => ({ ...current, Mandanten: [...current.Mandanten, {
        Mandant_Id: tenant.Id, MandantName: tenant.Name,
        Rollen: newRoles.map(code => ({ Kuerzel: code, Bezeichnung: roleLabel(code) })),
      }] }));
      setNewTenant(''); setNewRoles([]); return;
    }
    setBusy(true); setError('');
    try {
      const response = await api.post(`/sysadmin/benutzer/${form.Id}/mandanten`, { Mandant_Id: tenant.Id, Rollen: newRoles });
      setForm(response.data); setNewTenant(''); setNewRoles([]); onChanged(false);
    } catch (reason) { setError(failure(reason)); } finally { setBusy(false); }
  };

  const changeRoles = async (assignment, codes) => {
    if (creating) {
      setForm(current => ({ ...current, Mandanten: current.Mandanten.map(item => item.Mandant_Id === assignment.Mandant_Id
        ? { ...item, Rollen: codes.map(code => ({ Kuerzel: code, Bezeichnung: roleLabel(code) })) } : item) }));
      return;
    }
    if (!codes.length) { setError('Mindestens eine Rolle ist erforderlich.'); return; }
    setBusy(true); setError('');
    try {
      const response = await api.put(`/sysadmin/benutzer/${form.Id}/mandanten/${assignment.Mandant_Id}`,
        { Mandant_Id: assignment.Mandant_Id, Rollen: codes });
      setForm(response.data); onChanged(false);
    } catch (reason) { setError(failure(reason)); } finally { setBusy(false); }
  };

  const removeAssignment = async assignment => {
    if (!window.confirm(`Mandantenzuordnung „${assignment.MandantName}“ entfernen?`)) return;
    if (creating) {
      setForm(current => ({ ...current, Mandanten: current.Mandanten.filter(item => item.Mandant_Id !== assignment.Mandant_Id) }));
      return;
    }
    setBusy(true); setError('');
    try {
      await api.delete(`/sysadmin/benutzer/${form.Id}/mandanten/${assignment.Mandant_Id}`);
      setForm(current => ({ ...current, Mandanten: current.Mandanten.filter(item => item.Mandant_Id !== assignment.Mandant_Id) }));
      onChanged(false);
    } catch (reason) { setError(failure(reason)); } finally { setBusy(false); }
  };

  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md"><DialogTitle>
    {creating ? 'Benutzer anlegen' : 'Benutzer bearbeiten'}
  </DialogTitle><DialogContent><Stack spacing={2.5} sx={{ pt: 1 }}>
    {error && <Alert severity="error">{error}</Alert>}
    <Typography variant="subtitle1" fontWeight={700}>Benutzerdaten</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField required label="Benutzername" disabled={!creating} value={form.Benutzername}
        onChange={event => setForm({ ...form, Benutzername: event.target.value })} fullWidth inputProps={{ maxLength: 100 }} />
      <TextField label="Name" value={form.Anzeigename || ''} onChange={event => setForm({ ...form, Anzeigename: event.target.value })}
        fullWidth inputProps={{ maxLength: 200 }} />
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField required type="email" label="E-Mail" value={form.Email}
        onChange={event => setForm({ ...form, Email: event.target.value })} fullWidth inputProps={{ maxLength: 254 }} />
      <TextField select label="Status" value={form.Status} disabled={Boolean(form.Systemadministrator)}
        onChange={event => setForm({ ...form, Status: event.target.value })} fullWidth>
        <MenuItem value="Aktiv">Aktiv</MenuItem><MenuItem value="Gesperrt">Inaktiv</MenuItem>
      </TextField>
    </Stack>
    {creating && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <PasswordField label="Passwort" value={form.Passwort} onChange={value => setForm({ ...form, Passwort: value })} />
      <PasswordField label="Passwort wiederholen" value={form.PasswortWiederholung}
        onChange={value => setForm({ ...form, PasswortWiederholung: value })} />
    </Stack>}
    <Typography variant="subtitle1" fontWeight={700}>Mandantenzuordnungen</Typography>
    {!form.Mandanten.length && <Typography color="text.secondary">Noch keinem Mandanten zugeordnet.</Typography>}
    {form.Mandanten.map(assignment => <Stack key={assignment.Mandant_Id} direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ sm: 'center' }} spacing={1.5}>
      <Typography sx={{ flex: 1, minWidth: 180 }}>{assignment.MandantName}</Typography>
      <RoleSelect label={`Rollen für ${assignment.MandantName}`} value={assignment.Rollen.map(role => role.Kuerzel)}
        onChange={codes => changeRoles(assignment, codes)} />
      <Tooltip title="Zuordnung entfernen"><span><IconButton disabled={busy} color="error"
        aria-label={`Zuordnung ${assignment.MandantName} entfernen`} onClick={() => removeAssignment(assignment)}><Delete /></IconButton></span></Tooltip>
    </Stack>)}
    <Paper variant="outlined" sx={{ p: 2 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
      <TextField select size="small" label="Mandant hinzufügen" value={newTenant}
        onChange={event => setNewTenant(event.target.value)} sx={{ minWidth: 230 }}>
        {availableTenants.map(tenant => <MenuItem key={tenant.Id} value={tenant.Id}>{tenant.Name}</MenuItem>)}
      </TextField><RoleSelect label="Rollen für neue Zuordnung" value={newRoles} onChange={setNewRoles} />
      <Button startIcon={<Add />} disabled={busy || !newTenant || !newRoles.length} onClick={addAssignment}>Hinzufügen</Button>
    </Stack></Paper>
  </Stack></DialogContent><DialogActions><Button disabled={busy} onClick={onClose}>Abbrechen</Button>
    <Button variant="contained" disabled={busy || !form.Benutzername || !form.Email || (creating && (!form.Passwort || !form.PasswortWiederholung))}
      onClick={saveUser}>Speichern</Button></DialogActions></Dialog>;
}

export default function SysAdminUsers() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(undefined);
  const [passwordUser, setPasswordUser] = useState(null);
  const load = async () => {
    setLoading(true); setError('');
    try {
      const [userResponse, tenantResponse] = await Promise.all([api.get('/sysadmin/benutzer'), api.get('/sysadmin/mandanten')]);
      setUsers(userResponse.data); setTenants(tenantResponse.data);
    } catch (reason) { setError(failure(reason)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('de');
    if (!needle) return users;
    return users.filter(user => [user.Benutzername, user.Anzeigename, user.Email]
      .some(value => value?.toLocaleLowerCase('de').includes(needle)));
  }, [users, search]);
  const changed = (close = true) => { load(); if (close) setEditing(undefined); };
  return <Box><PageHeader title="Benutzer" action={<Button variant="contained" size="small" startIcon={<Add />}
    onClick={() => setEditing(null)}>Hinzufügen</Button>} />
    <TextField size="small" label="Nach Name oder Benutzername suchen" value={search}
      onChange={event => setSearch(event.target.value)} sx={{ minWidth: { xs: '100%', sm: 340 }, mb: 2 }} />
    {loading && <LoadingIndicator label="Benutzer werden geladen …" />}{error && <Alert severity="error">{error}</Alert>}
    <TableContainer component={Paper}><Table size="small"><TableHead><TableRow>
      {['Benutzername', 'Name', 'E-Mail', 'Status', 'Mandanten und Rollen', 'Aktion'].map(label => <TableCell key={label}>{label}</TableCell>)}
    </TableRow></TableHead><TableBody>{filtered.map(user => <TableRow key={user.Id}>
      <TableCell>{user.Benutzername}{user.Systemadministrator && <Chip label="Systemadministrator" size="small" color="primary" sx={{ ml: 1 }} />}</TableCell>
      <TableCell>{user.Anzeigename || '–'}</TableCell><TableCell>{user.Email}</TableCell>
      <TableCell><Chip size="small" label={user.Status === 'Aktiv' ? 'Aktiv' : 'Inaktiv'}
        sx={documentStatusChipSx(user.Status === 'Aktiv' ? 'FREIGEGEBEN' : 'STILLGELEGT')} /></TableCell>
      <TableCell>{user.Mandanten.length ? <Stack spacing={0.5}>{user.Mandanten.map(assignment => <Box key={assignment.Mandant_Id}>
        <Typography variant="body2" fontWeight={600}>{assignment.MandantName}</Typography>
        <Typography variant="caption" color="text.secondary">{assignment.Rollen.map(role => role.Bezeichnung).join(', ') || 'Keine Rolle'}</Typography>
      </Box>)}</Stack> : '–'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><Tooltip title="Bearbeiten"><IconButton aria-label={`Benutzer ${user.Benutzername} bearbeiten`}
        onClick={() => setEditing(user)}><Edit /></IconButton></Tooltip><Tooltip title="Passwort setzen"><IconButton
        aria-label={`Passwort für ${user.Benutzername} setzen`} onClick={() => setPasswordUser(user)}><Key /></IconButton></Tooltip></TableCell>
    </TableRow>)}</TableBody></Table></TableContainer>
    {editing !== undefined && <UserDialog user={editing} tenants={tenants} onClose={() => setEditing(undefined)} onChanged={changed} />}
    {passwordUser && <PasswordDialog user={passwordUser} onClose={() => setPasswordUser(null)} />}
  </Box>;
}
